/**
 * Pesado 58.5 — Cart Drawer Enhancements
 * -------------------------------------------------------------
 * 1. Multi-currency free shipping progress bar
 *    - Only shows for USD, AUD, NZD, KRW (per-currency thresholds)
 *    - Converts cart total using the same Currency.convert() engine
 *      Kalles already loads via currencies.js
 *    - Re-runs on the theme's own "currency:update" event, AND
 *      re-fetches /cart.js whenever the visible cart total changes,
 *      so it never gets out of sync with quantity/add/remove actions
 *      even if we don't know every internal cart-refresh event name.
 *
 * 2. Announcement bar rotation (simple fade cycle, any number of blocks)
 *
 * 3. Upsell slider: keeps the "Add" button's variant id + displayed
 *    price in sync with the option <select>, then relies on Kalles'
 *    own [data-action-atc] handler (same one used by the gift-wrap
 *    button) to actually perform the AJAX add-to-cart.
 *
 * Load this file after currencies.js / global theme JS, e.g. via
 * {{ 'cart-drawer-enhancements.js' | asset_url | script_tag }}
 * in theme.liquid, or defer-loaded near your other cart scripts.
 * -------------------------------------------------------------
 */
(function () {
  'use strict';

  var SUPPORTED_CURRENCIES = ['USD', 'AUD', 'NZD', 'KRW'];
  var CURRENCY_SYMBOLS = { USD: '$', AUD: '$', NZD: '$', KRW: '₩' };

  /* ============================================================
     1. MULTI-CURRENCY FREE SHIPPING BAR
  ============================================================ */

  function getCurrentCurrency() {
    try {
      if (window.T4SThemeSP && typeof T4SThemeSP.storageCurrency === 'function') {
        var fromTheme = T4SThemeSP.storageCurrency();
        if (fromTheme) return String(fromTheme).toUpperCase();
      }
    } catch (e) {}

    try {
      var stored = localStorage.getItem('T4Currency');
      if (stored) return stored.toUpperCase();
    } catch (e) {}

    var currentEl = document.querySelector('[data-currency-wrap] [data-current]');
    if (currentEl && currentEl.textContent) {
      return currentEl.textContent.trim().toUpperCase();
    }

    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      return Shopify.currency.active.toUpperCase();
    }

    return null;
  }

  function formatMoneyForBar(amountMainUnit, currency) {
    var symbol = CURRENCY_SYMBOLS[currency] || '';
    if (currency === 'KRW') {
      return symbol + Math.round(amountMainUnit).toLocaleString('en-US');
    }
    return symbol + amountMainUnit.toFixed(2) + ' ' + currency;
  }

  function updateShipBar() {
    var wrap = document.querySelector('[data-multi-currency-shipbar]');
    if (!wrap) return;

    var currency = getCurrentCurrency();

    if (!currency || SUPPORTED_CURRENCIES.indexOf(currency) === -1) {
      wrap.classList.add('t4s-d-none');
      return;
    }

    var baseCurrency = wrap.getAttribute('data-base-currency');
    var totalCents = parseFloat(wrap.getAttribute('data-cart-total-cents')) || 0;

    var thresholds = {};
    try {
      thresholds = JSON.parse(wrap.getAttribute('data-thresholds'));
    } catch (e) {}

    var threshold = thresholds[currency];
    if (!threshold) {
      wrap.classList.add('t4s-d-none');
      return;
    }

    var convertedMainUnit;
    try {
      if (window.Currency && typeof Currency.convert === 'function' && currency !== baseCurrency) {
        convertedMainUnit = Currency.convert(totalCents, baseCurrency, currency) / 100;
      } else {
        convertedMainUnit = totalCents / 100;
      }
    } catch (e) {
      convertedMainUnit = totalCents / 100;
    }

    wrap.classList.remove('t4s-d-none');

    var textEl = wrap.querySelector('[data-mcsb-text]');
    var fillEl = wrap.querySelector('[data-mcsb-fill]');
    var badgeEl = wrap.querySelector('[data-mcsb-badge]');

    var pct = Math.max(0, Math.min(100, (convertedMainUnit / threshold) * 100));
    if (fillEl) fillEl.style.width = pct + '%';
    if (badgeEl) badgeEl.style.left = pct + '%';

    if (convertedMainUnit >= threshold) {
      wrap.classList.add('is--complete');
      if (textEl) textEl.innerHTML = wrap.getAttribute('data-msg-success');
    } else {
      wrap.classList.remove('is--complete');
      var remaining = threshold - convertedMainUnit;
      var msgTemplate = wrap.getAttribute('data-msg-remaining') || 'You are [amount] away from Free shipping discount';
      var msg = msgTemplate.replace('[amount]', '<strong>' + formatMoneyForBar(remaining, currency) + '</strong>');
      if (textEl) textEl.innerHTML = msg;
    }
  }

  function refreshCartTotalThenUpdateBar() {
    var wrap = document.querySelector('[data-multi-currency-shipbar]');
    var timerWrap = document.querySelector('[data-cart-timer]');
    if (!wrap && !timerWrap) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (wrap) {
          wrap.setAttribute('data-cart-total-cents', cart.total_price);
          updateShipBar();
        }
        if (timerWrap) {
          timerWrap.setAttribute('data-cart-item-count', cart.item_count);
          tickReservationTimer();
        }
      })
      .catch(function () {
        updateShipBar();
      });
  }

  /**
   * Watching a specific DOM node (e.g. the total price element) for
   * changes is fragile: many themes fully replace that node (rather
   * than editing its text) whenever the cart AJAX-updates, silently
   * leaving any previously-attached observer watching a dead,
   * detached element. Instead, we intercept the actual network calls
   * that mutate the cart (add / change / update / clear) — this
   * works regardless of how the theme chooses to re-render the DOM
   * afterward, and covers both fetch() and jQuery/XHR-based themes.
   */
  var CART_MUTATION_URL_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var networkInterceptBound = false;

  function bindCartNetworkInterception() {
    if (networkInterceptBound) return;
    networkInterceptBound = true;

    // --- fetch() ---
    if (window.fetch) {
      var originalFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var isCartMutation = CART_MUTATION_URL_PATTERN.test(url);
        var promise = originalFetch.apply(this, arguments);
        if (isCartMutation) {
          promise.then(function () {
            setTimeout(refreshCartTotalThenUpdateBar, 60);
          }).catch(function () {});
        }
        return promise;
      };
    }

    // --- XMLHttpRequest (covers jQuery.ajax, which Kalles uses internally) ---
    if (window.XMLHttpRequest) {
      var OriginalXHR = window.XMLHttpRequest;
      var originalOpen = OriginalXHR.prototype.open;
      var originalSend = OriginalXHR.prototype.send;

      OriginalXHR.prototype.open = function (method, url) {
        this._t4sCartMutation = CART_MUTATION_URL_PATTERN.test(url || '');
        return originalOpen.apply(this, arguments);
      };

      OriginalXHR.prototype.send = function () {
        if (this._t4sCartMutation) {
          this.addEventListener('loadend', function () {
            setTimeout(refreshCartTotalThenUpdateBar, 60);
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  /* ============================================================
     2. ANNOUNCEMENT BAR ROTATION
  ============================================================ */

  /* ============================================================
     4. CART RESERVATION COUNTDOWN TIMER
     ---------------------------------------------------------
     Purely a front-end urgency UI element (Shopify does not
     actually reserve inventory). Persists across page reloads
     and drawer re-opens via localStorage, keyed to an expiry
     timestamp. Resets automatically whenever the cart is empty.
  ============================================================ */

  var RESERVATION_KEY = 'pesado_cart_reservation_expiry';
  var reservationIntervalId = null;

  function formatCountdown(ms) {
    var totalSeconds = Math.max(0, Math.floor(ms / 1000));
    var mins = Math.floor(totalSeconds / 60);
    var secs = totalSeconds % 60;
    return mins + ':' + (secs < 10 ? '0' : '') + secs;
  }

  function tickReservationTimer() {
    var wrap = document.querySelector('[data-cart-timer]');
    if (!wrap) return;

    var itemCount = parseInt(wrap.getAttribute('data-cart-item-count'), 10) || 0;
    var countdownEl = wrap.querySelector('[data-timer-countdown]');

    if (itemCount === 0) {
      try { localStorage.removeItem(RESERVATION_KEY); } catch (e) {}
      wrap.classList.add('t4s-d-none');
      return;
    }

    var expiry = null;
    try { expiry = parseInt(localStorage.getItem(RESERVATION_KEY), 10); } catch (e) {}

    var minutes = parseFloat(wrap.getAttribute('data-timer-minutes')) || 10;

    if (!expiry || isNaN(expiry) || expiry <= Date.now()) {
      expiry = Date.now() + minutes * 60 * 1000;
      try { localStorage.setItem(RESERVATION_KEY, expiry); } catch (e) {}
    }

    wrap.classList.remove('t4s-d-none');

    var remaining = expiry - Date.now();
    if (remaining <= 0) {
      wrap.classList.add('is--expired');
      if (countdownEl) countdownEl.textContent = '0:00';
    } else {
      wrap.classList.remove('is--expired');
      if (countdownEl) countdownEl.textContent = formatCountdown(remaining);
    }
  }

  function bindReservationTimer() {
    tickReservationTimer();
    if (reservationIntervalId) return; // only ever need one global 1s ticker
    reservationIntervalId = setInterval(tickReservationTimer, 1000);
  }

  /* ============================================================
     3. UPSELL VARIANT / PRICE SYNC
  ============================================================ */

  function initUpsellVariantSync() {
    document.addEventListener('change', function (e) {
      var select = e.target.closest('[data-upsell-variant-select]');
      if (!select) return;

      var itemWrap = select.closest('[data-upsell-item]');
      if (!itemWrap) return;

      var selectedOption = select.options[select.selectedIndex];
      var variantId = selectedOption.getAttribute('data-variant-id');
      var variantPrice = selectedOption.getAttribute('data-variant-price');

      var addBtn = itemWrap.querySelector('[data-upsell-add]');
      if (addBtn && variantId) {
        addBtn.setAttribute('data-variant-id', variantId);
      }

      var priceWrap = itemWrap.querySelector('[data-upsell-price]');
      if (priceWrap && variantPrice && window.Shopify && typeof Shopify.formatMoney === 'function') {
        var moneyFormat = (window.T4Sconfigs && T4Sconfigs.moneyFormat) || '${{amount}}';
        priceWrap.innerHTML = Shopify.formatMoney(variantPrice, moneyFormat);
      }
    });
  }

  /* ============================================================
     INIT
     ---------------------------------------------------------
     On live (non-design-mode) pages, Kalles only injects the cart
     drawer's inner HTML via AJAX (on first open, on add-to-cart,
     on currency switch, etc.) — it is NOT present at DOMContentLoaded.
     Rather than guess every internal event name that could trigger
     that injection, we watch the whole document for DOM changes and
     re-bind whenever our elements (re)appear or get replaced.
  ============================================================ */

  var announceIntervalId = null;

  function bindAnnouncementBar() {
    var bar = document.querySelector('[data-cart-announce]');
    if (!bar || bar.getAttribute('data-announce-bound') === '1') return;
    bar.setAttribute('data-announce-bound', '1');

    var items = bar.querySelectorAll('[data-announce-item]');
    if (items.length < 2) return;

    if (announceIntervalId) clearInterval(announceIntervalId);

    var index = 0;
    announceIntervalId = setInterval(function () {
      items[index].classList.remove('is--active');
      index = (index + 1) % items.length;
      items[index].classList.add('is--active');
    }, 4000);
  }

  function runAllUpdates() {
    updateShipBar();
    bindCartNetworkInterception();
    bindAnnouncementBar();
    bindReservationTimer();
  }

  function initAll() {
    runAllUpdates();
    initUpsellVariantSync(); // event-delegated on document, safe to bind once
    document.body.addEventListener('currency:update', runAllUpdates);

    // Watch the whole document for the cart drawer being injected,
    // replaced, or updated (covers first AJAX load, add-to-cart,
    // quantity changes, currency switches — regardless of which
    // internal event name triggers each one).
    if (window.MutationObserver) {
      var debounceTimer = null;
      var observer = new MutationObserver(function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(runAllUpdates, 120);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Pragmatic safety net: a few delayed retries in case the drawer
    // content loads slightly after this script runs, before any
    // mutation would otherwise be observed.
    [300, 800, 1500, 3000].forEach(function (delay) {
      setTimeout(runAllUpdates, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.T4SUpdateShipBar = updateShipBar;
})();
